/* ============================================
   🎯 SYSTÈME D'ACCORDÉON CENTRALISÉ - JAVASCRIPT
   ============================================
   Fichier JS centralisé pour tous les accordéons du thème
   
   Utilisation:
   initAccordion(selector, options)
   
   Options:
   - closeOthers: false      // Ferme les autres accordéons (mode accordion)
   - responsive: false       // Active le mode responsive
   - desktopAlwaysOpen: false // Desktop toujours ouvert (nécessite responsive: true)
   - context: document       // Contexte de recherche (utile pour les éléments dynamiques)
   
   ============================================ */

/**
 * Initialise un système d'accordéon sur les éléments correspondant au sélecteur
 * @param {string} selector - Sélecteur CSS des boutons d'accordéon
 * @param {Object} options - Options de configuration
 */
function initAccordion(selector, options = {}) {
  const {
    closeOthers = false,
    responsive = false,
    desktopAlwaysOpen = false,
    context = document,
  } = options;

  const buttons = context.querySelectorAll(selector);
  if (buttons.length === 0) return;

  /**
   * Vérifie si on est en mode mobile (pour le mode responsive)
   */
  function isMobile() {
    return window.innerWidth <= 749;
  }

  /**
   * Initialise l'état des accordéons
   */
  function initState() {
    // Récupérer les boutons actuels dans le DOM
    const currentButtons = context.querySelectorAll(selector);

    currentButtons.forEach((button) => {
      const icon = button.querySelector(".accordion-icon");
      // Chercher le wrapper d'accordéon (footer ou menu-drawer)
      const accordionWrapper = button.closest(
        ".footer__accordion, .menu-drawer__accordion"
      );
      const defaultOpen =
        accordionWrapper?.getAttribute("data-default-open") === "true";

      // Mode responsive : gérer mobile vs desktop
      if (responsive) {
        if (isMobile()) {
          // Mobile : fermé par défaut, SAUF si data-default-open="true"
          if (defaultOpen) {
            button.setAttribute("aria-expanded", "true");
            if (icon) icon.classList.add("active");
          } else {
            button.setAttribute("aria-expanded", "false");
            if (icon) icon.classList.remove("active");
          }
        } else {
          // Desktop : toujours ouvert
          button.setAttribute("aria-expanded", "true");
          if (icon) icon.classList.add("active");
        }
      } else {
        // Mode normal : respecter l'état aria-expanded existant OU data-default-open
        const currentState = button.getAttribute("aria-expanded") || "false";
        if (defaultOpen || currentState === "true") {
          button.setAttribute("aria-expanded", "true");
          if (icon) icon.classList.add("active");
        }
      }
    });
  }

  /**
   * Gère le clic sur un accordéon
   */
  function handleClick(button, e) {
    // Mode responsive + desktop : pas cliquable
    if (responsive && desktopAlwaysOpen && !isMobile()) {
      return;
    }

    e.preventDefault();
    e.stopPropagation();

    const isOpen = button.getAttribute("aria-expanded") === "true";
    const icon = button.querySelector(".accordion-icon");

    // Mode closeOthers : fermer tous les autres
    if (closeOthers) {
      // Récupérer les boutons actuels dans le DOM
      const currentButtons = context.querySelectorAll(selector);

      currentButtons.forEach((otherButton) => {
        if (otherButton !== button) {
          otherButton.setAttribute("aria-expanded", "false");
          const otherIcon = otherButton.querySelector(".accordion-icon");
          if (otherIcon) otherIcon.classList.remove("active");
        }
      });
    }

    // Toggle l'accordéon cliqué
    button.setAttribute("aria-expanded", isOpen ? "false" : "true");

    if (icon) {
      if (isOpen) {
        icon.classList.remove("active");
      } else {
        icon.classList.add("active");
      }
    }
  }

  /**
   * Attache les event listeners (avec clonage pour éviter les doublons)
   */
  function attachListeners() {
    // Récupérer les boutons actuels dans le DOM
    const currentButtons = context.querySelectorAll(selector);

    currentButtons.forEach((button) => {
      // Vérifier que le bouton a bien un parent
      if (!button.parentNode) return;

      // Cloner le bouton pour supprimer les anciens listeners
      const newButton = button.cloneNode(true);
      button.parentNode.replaceChild(newButton, button);

      // Ajouter le nouveau listener
      newButton.addEventListener("click", (e) => handleClick(newButton, e));
    });
  }

  // Initialiser l'état
  initState();

  // Attacher les listeners
  attachListeners();

  // Mode responsive : réinitialiser au redimensionnement
  if (responsive) {
    let resizeTimer;
    window.addEventListener("resize", function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        initState();
        attachListeners(); // Ré-attacher pour avoir les bons boutons
      }, 250);
    });
  }
}

/* ============================================
   INITIALISATION AUTOMATIQUE
   ============================================ */

(function () {
  function autoInit() {
    // 1. FOOTER - Mode responsive avec desktop toujours ouvert
    const footerButtons = document.querySelectorAll(".footer__menu-title");
    if (footerButtons.length > 0) {
      initAccordion(".footer__menu-title", {
        responsive: true,
        desktopAlwaysOpen: true,
      });
    }

    // 2. PRODUCT FAQ - Mode accordion (ferme les autres)
    const faqContainers = document.querySelectorAll(
      ".product-faq-wrapper:not([data-initialized])"
    );
    faqContainers.forEach((faqContainer) => {
      faqContainer.dataset.initialized = "true";
      initAccordion(".product-faq-toggle", {
        closeOthers: true,
        context: faqContainer,
      });
    });

    // Note: Header Drawer est géré par son propre script car il utilise un custom element
    // et a besoin d'être initialisé à l'ouverture du drawer
  }

  // Initialiser au chargement
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", autoInit);
  } else {
    autoInit();
  }

  // Réessayer pour les éléments chargés dynamiquement
  setTimeout(autoInit, 100);
  setTimeout(autoInit, 500);
})();

/* ============================================
   EXPORT POUR UTILISATION EXTERNE
   ============================================ */

// Rendre la fonction disponible globalement pour les cas spécifiques
window.initAccordion = initAccordion;
